import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BatchRequestService } from './batch-request.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  mergeEnvToBaseUrls,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-batch-request',
    templateUrl: './batch-request.component.html',
    styleUrls: ['./batch-request.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class BatchRequestComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  filterParams: any = {};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  modalTitle = "Request Input";
  monitoringData: any = {};
  showJsonLoader = false;
  fileToDownload = "";
  entityList: any[] = [];

  constructor(
    private _snackBar: MatSnackBar,
    private batchRequestService: BatchRequestService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = mergeEnvToBaseUrls(this.baseURLs, this.gtwGlobal.GENERAL_CONFIG) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.appService = {
      USER_SETTINGS: this.gtwApp.USER_SETTINGS,
      SERVER_CONFIG: this.gtwApp.SERVER_CONFIG,
    };
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0 &&
          this.GENERAL_CONFIG && this.globalService) {
        this.setupEntityData();
        this.fetchMonitoringData();
      }
    } catch (e) {
      console.error('Error in BatchRequest init:', e);
    }
  }

  private setupEntityData() {
    this.entityList = [
      {key: "Request ID", value: this.rowData.REQUEST_ID || 'N/A'},
      {key: "Status", value: this.rowData.STATUS || 'N/A'},
      {key: "Created Date", value: this.rowData.CREATED_DATE || 'N/A'}
    ];
  }

  fetchMonitoringData() {
    this.showJsonLoader = true;

    const params = {
      request_id: this.rowData.REQUEST_ID,
      ist_tkn: this.rowData.IST_REQ_TKN
    };

    this.batchRequestService.getMonitoringData(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        try {
          const tempMonitoringData = safeJsonInput<any>(response.jsonObject.clob_data, null, 'batch-request.parse1');
          this.fileToDownload = tempMonitoringData.file_name || '';

          if (!Array.isArray(tempMonitoringData)) {
            Object.keys(tempMonitoringData).forEach(key => {
              const value = tempMonitoringData[key];
              if (typeof value === 'string' && (value.includes("{") || value.includes("["))) {
                try {
                  tempMonitoringData[key] = safeJsonInput<any>(value, null, 'batch-request.parse2');
                } catch (e) {
                  console.warn('Failed to parse JSON for key:', key);
                }
              }
            });
          }

          this.monitoringData = this.objToJsonView(tempMonitoringData);
          this.showJsonLoader = false;
        } catch (e) {
          console.error('Error processing monitoring data:', e);
          this.showJsonLoader = false;
          this._snackBar.open('Error processing request data', 'Close', { duration: 4000 });
        }
      },
      (error: any) => {
        console.error('Error fetching monitoring data:', error);
        this.showJsonLoader = false;
        this._snackBar.open('Error fetching request data', 'Close', { duration: 4000 });
      }
    );
  }

  objToJsonView(obj: any): string {
    try {
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      console.error('Error converting object to JSON view:', e);
      return 'Error displaying data';
    }
  }

  downloadFile() {
    if (this.fileToDownload && this.GENERAL_CONFIG && this.appService.USER_SETTINGS) {
      const downloadURL = `${this.GENERAL_CONFIG.base_url}/download?file_name=${this.fileToDownload}&client_key=${this.appService.USER_SETTINGS.user.client_key}`;
      window.open(downloadURL, '_blank');
    } else {
      this._snackBar.open('No file available for download', 'Close', { duration: 4000 });
    }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  reset() {
    this.fetchMonitoringData();
  }
}
